import React, { ReactElement, ReactNode } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useQuery } from 'react-query';

import {
  BackButton,
  BusinessNotice,
  Container,
  DetailHeader,
  DetailTable,
  LabelCell,
  SectionHeaderCell,
  SectionTitle,
  ValueCell
} from '../CompanyInfo/styles';
import {
  TAccountInfo,
  TBizInfo,
  TClientContractDetail,
  TContractInfo,
  TManager,
  TServiceCharge,
} from '../CompanyInfo/companyInfoTypes';
import getClientContractDetail from './state';

interface RouteParams {
  clientContractIdx: string;
}

type DetailRow = [string, ReactNode]; // [label, value] — named tuple은 TS 4.0+ 전용이라 제거 (현 ts-loader는 typescript@3.9.10)

interface DetailSection {
  title: string;
  rows: DetailRow[];
}

const renderValue = (value?: string | number): string => {
  if (value === 0) return '0';
  return value ? String(value) : '-';
};

const formatPeriod = (info?: TContractInfo): string => {
  if (!info) return '-';
  const start = info.contractStartDate ?? '';
  const end = info.contractEndDate ?? '';
  if (!start && !end) return '-';
  return `${start} ~ ${end}`;
};

const formatBankAccount = (info?: TAccountInfo): string => {
  if (!info) return '-';
  const { vendysBankName, vendysAccountNumber, vendysBankAccountHolder } = info;
  if (!vendysBankName && !vendysAccountNumber && !vendysBankAccountHolder) return '-';
  return `${vendysBankName ?? ''} ${vendysAccountNumber ?? ''} (${vendysBankAccountHolder ?? ''})`;
};

const formatCharge = (charge?: TServiceCharge): string => {
  if (!charge) return '-';
  const amount = charge.unit === 'PERCENT' ? charge.charge : charge.charge.toLocaleString();
  const unitName = charge.unitName ?? (charge.unit === 'PERCENT' ? '%' : '원');
  return `${amount} (${unitName})`;
};

const formatPayDay = (info?: TAccountInfo): string => {
  if (!info?.payDay) return '-';
  const { payDay, payDayBaseTypeName, payDayAdjustmentTypeName } = info;
  return `${payDayBaseTypeName ?? ''} : ${payDay}일${
    payDayAdjustmentTypeName ? ` / 휴일 시 : ${payDayAdjustmentTypeName}` : ''
  }`;
};

const buildBusinessInfoRows = (biz?: TBizInfo): DetailRow[] => [
  ['상호명', renderValue(biz?.bizName)],
  ['사업자 번호', renderValue(biz?.bizNumber)],
  ['종사업장 번호', renderValue(biz?.bizSubNumber)],
  ['대표자 명', renderValue(biz?.bizPresidentName)],
  ['업태', renderValue(biz?.bizCategory)],
  ['종목', renderValue(biz?.bizItem)],
  ['사업장 주소', renderValue([biz?.address, biz?.addressDetail].filter(Boolean).join(' '))]
];

const buildContractSections = (detail?: TClientContractDetail): DetailSection[] => {
  const contractInfo = detail?.contractInfo;
  const settlementInfo = detail?.settlementInfo;
  const accountInfo = detail?.accountInfo;

  return [
    {
      title: '계약 정보',
      rows: [
        ['서비스', renderValue(contractInfo?.contractTypeName)],
        ['계약 기간', formatPeriod(contractInfo)]
      ]
    },
    {
      title: '정산 정보',
      rows: [
        ['정산 범위', renderValue(settlementInfo?.orgFullName)],
        ['서비스 주기 시작일', renderValue(settlementInfo?.billingCycleDay)],
        ['작성일자 옵션', renderValue(settlementInfo?.evidenceWriteDateOptionName)],
        ['증빙 발행 방식', renderValue(settlementInfo?.evidenceIssuedTypeName)]
      ]
    },
    {
      title: '계좌 정보',
      rows: [
        ['입금 계좌', formatBankAccount(accountInfo)],
        ['입금자 명', renderValue(accountInfo?.depositAccountHolder)],
        ['입금 기한', formatPayDay(accountInfo)]
      ]
    }
  ];
};

const CHARGE_COLUMNS: Array<{ key: 'chargeTypeName' | 'amount'; label: string }> = [
  { key: 'chargeTypeName', label: '이용료 유형' },
  { key: 'amount', label: '금액' }
];

const MANAGER_COLUMNS: Array<{ key: keyof TManager; label: string }> = [
  { key: 'managerName', label: '담당자명' },
  { key: 'managerEmail', label: '이메일' },
  { key: 'managerPhone', label: '전화번호' }
];

function CompanyInfoDetail({ history, match }: RouteComponentProps<RouteParams>): ReactElement {
  const { clientContractIdx } = match.params;

  const { data: detail } = useQuery<TClientContractDetail>(
    ['getClientContractDetail', clientContractIdx],
    () => getClientContractDetail(clientContractIdx),
    { enabled: Boolean(clientContractIdx) }
  );

  const contractSections = buildContractSections(detail);
  const businessRows = buildBusinessInfoRows(detail?.bizInfo);
  const charges: TServiceCharge[] = detail?.contractInfo?.serviceCharges ?? [];
  const managers: TManager[] = detail?.managers ?? [];

  const handleBack = () => {
    history.push('/service/management/company/info');
  };

  return (
    <Container>
      <DetailHeader>
        <SectionTitle>이용 서비스</SectionTitle>
        <BackButton type="button" onClick={handleBack}>
          목록
        </BackButton>
      </DetailHeader>

      <DetailTable>
        <colgroup>
          <col style={{ width: '180px' }} />
          <col />
        </colgroup>
        <tbody>
          {contractSections.map(({ title, rows }) => (
            <React.Fragment key={title}>
              <tr>
                <SectionHeaderCell colSpan={2}>{title}</SectionHeaderCell>
              </tr>
              {rows.map(([label, value]) => (
                <tr key={`${title}-${label}`}>
                  <LabelCell>{label}</LabelCell>
                  <ValueCell>{value}</ValueCell>
                </tr>
              ))}
            </React.Fragment>
          ))}

          <tr>
            <SectionHeaderCell colSpan={2}>이용료 정보</SectionHeaderCell>
          </tr>
          <tr>
            {CHARGE_COLUMNS.map(({ key, label }) => (
              <LabelCell as="th" key={key}>
                {label}
              </LabelCell>
            ))}
          </tr>
          {charges.length === 0 ? (
            <tr>
              <ValueCell colSpan={CHARGE_COLUMNS.length} style={{ textAlign: 'center' }}>
                서비스 이용료 무료
              </ValueCell>
            </tr>
          ) : (
            charges.map((charge, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`charge-${idx}`}>
                <ValueCell>{renderValue(charge.chargeTypeName)}</ValueCell>
                <ValueCell>{formatCharge(charge)}</ValueCell>
              </tr>
            ))
          )}
        </tbody>
      </DetailTable>

      <SectionTitle>사업자 기본 정보</SectionTitle>
      <BusinessNotice>
        사업자 정보의 변경을 원하실 경우 사업자 등록증 1부와 변경사항을{' '}
        <a href="mailto:account@vendys.co.kr">account@vendys.co.kr</a>로 전달해 주세요.
      </BusinessNotice>
      <DetailTable>
        <tbody>
          {businessRows.map(([label, value]) => (
            <tr key={label}>
              <LabelCell>{label}</LabelCell>
              <ValueCell>{value}</ValueCell>
            </tr>
          ))}
        </tbody>
      </DetailTable>

      <SectionTitle>담당자 정보</SectionTitle>
      <DetailTable>
        <thead>
          <tr>
            {MANAGER_COLUMNS.map(({ key, label }) => (
              <LabelCell as="th" key={key}>
                {label}
              </LabelCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {managers.length === 0 ? (
            <tr>
              <ValueCell colSpan={MANAGER_COLUMNS.length} style={{ textAlign: 'center' }}>
                등록된 담당자가 없습니다.
              </ValueCell>
            </tr>
          ) : (
            managers.map((manager, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${manager.managerEmail ?? 'manager'}-${idx}`}>
                {MANAGER_COLUMNS.map(({ key }) => (
                  <ValueCell key={key}>{renderValue(manager[key])}</ValueCell>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </DetailTable>
    </Container>
  );
}

export default withRouter(CompanyInfoDetail);
